import { useEffect, useRef, useState } from 'react';
import { authAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';


const TABS = [
  { key: 'account',  label: 'Account Details' },
  { key: 'password', label: 'Change Password' },
];

export default function ManagerProfile() {
  const { user, updateProfile: storeUpdateProfile } = useAuthStore();
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activeTab, setActiveTab] = useState('account');

  useEffect(() => {
    authAPI.profile()
      .then(setProfile)
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
    );
  }

  const displayName = profile?.full_name || user?.full_name || 'Manager';

  const handlePictureUpdated = (url) => {
    setProfile(p => ({ ...p, picture: url }));
  };

  const handleNameUpdated = (newName) => {
    setProfile(p => ({ ...p, full_name: newName }));
    const updated = { ...user, full_name: newName };
    localStorage.setItem('user', JSON.stringify(updated));
  };

  return (
    <div className="space-y-5">

      {/* ── Header card with picture ── */}
      <ProfileHeader profile={profile} displayName={displayName} onPictureUpdated={handlePictureUpdated} />

      {/* ── Tabs ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex-1 px-2 py-3 text-xs font-semibold transition-colors sm:px-5 sm:text-sm
                ${activeTab === t.key ? 'border-b-2 border-orange-500 text-orange-600' : 'text-slate-500 hover:text-slate-800'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-5">
          {activeTab === 'account'  && <AccountSection profile={profile} onNameUpdated={handleNameUpdated} />}
          {activeTab === 'password' && <ChangePasswordForm />}
        </div>
      </div>
    </div>
  );
}

// ── Profile Header with picture upload ──────────────────────────────────────

function ProfileHeader({ profile, displayName, onPictureUpdated }) {
  const fileRef  = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [picError, setPicError]   = useState('');
  const initial    = displayName[0]?.toUpperCase() ?? 'M';
  const pictureUrl = profile?.picture || null;

  const ROLE_LABELS = { admin: 'Administrator', finance: 'Finance Officer', board: 'Board Member' };
  const roleLabel = ROLE_LABELS[profile?.role] || profile?.role || '';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { setPicError('Only JPG, PNG or WebP allowed.'); return; }
    if (file.size > 3 * 1024 * 1024)  { setPicError('File must be under 3 MB.'); return; }

    setPicError('');
    setUploading(true);
    try {
      const res = await authAPI.uploadPicture(file);
      onPictureUpdated(res.picture);
    } catch (err) {
      setPicError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#f07415,#0f172a)] p-6 text-white shadow-lg">
      <div className="flex flex-wrap items-center gap-5">

        {/* Avatar / Picture */}
        <div className="relative shrink-0">
          <div className="h-20 w-20 rounded-2xl overflow-hidden bg-white/20 backdrop-blur-sm flex items-center justify-center">
            {pictureUrl ? (
              <img
                src={pictureUrl}
                alt={displayName}
                className="h-full w-full object-cover"
                onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <span className={`text-2xl font-black text-white ${pictureUrl ? 'hidden' : 'flex'} items-center justify-center h-full w-full`}>
              {initial}
            </span>
          </div>

          {/* Camera button */}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Change profile picture"
            className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-orange-500 hover:bg-orange-500 border-2 border-white flex items-center justify-center shadow-md transition-colors disabled:opacity-60">
            {uploading ? (
              <svg className="h-3 w-3 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>

        {/* Name + badges */}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{displayName}</h1>
          <div className="mt-1.5 flex flex-wrap gap-2">
            <span className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold capitalize">{roleLabel}</span>
            <span className="rounded-full bg-emerald-400/30 text-emerald-100 px-3 py-0.5 text-xs font-semibold capitalize">
              {profile?.status || 'active'}
            </span>
          </div>
          {picError && (
            <p className="mt-2 text-xs text-red-300">{picError}</p>
          )}
          {!picError && (
            <p className="mt-2 text-[11px] text-white/50">
              {uploading ? 'Uploading…' : 'Click the camera icon to change your profile picture'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Account Section ──────────────────────────────────────────────────────────

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</span>
      <span className="ml-4 text-right text-sm font-semibold text-slate-800">{value || '—'}</span>
    </div>
  );
}

function AccountSection({ profile, onNameUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [phone, setPhone]       = useState(profile?.phone || '');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await authAPI.updateProfile({ full_name: fullName.trim(), phone: phone.trim() });
      onNameUpdated(fullName.trim());
      setSuccess('Profile updated successfully.');
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {error   && <div className="rounded-xl border border-red-200   bg-red-50   px-4 py-2.5 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700">{success}</div>}
      {!editing && !profile?.phone && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">
          Add your phone number so the system can reach you by SMS for important alerts (e.g. the CRDB VPN going down).
        </div>
      )}

      {editing ? (
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="0715465042"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
            />
            <p className="mt-1 text-xs text-slate-400">Used for SMS alerts, e.g. if the CRDB VPN connection drops.</p>
          </div>
          <InfoRow label="Email" value={profile?.email} />
          <InfoRow label="Role"  value={profile?.role}  />
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <button onClick={() => { setFullName(profile?.full_name || ''); setPhone(profile?.phone || ''); setEditing(true); setError(''); setSuccess(''); }}
              className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
              ✏ Edit Profile
            </button>
          </div>
          <div className="space-y-2">
            <InfoRow label="Full Name" value={profile?.full_name} />
            <InfoRow label="Phone"     value={profile?.phone} />
            <InfoRow label="Email"     value={profile?.email} />
            <InfoRow label="Role"      value={profile?.role} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Change Password ──────────────────────────────────────────────────────────

function ChangePasswordForm() {
  const [form, setForm]       = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError]     = useState('');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (form.new_password !== form.confirm_password) { setError('New passwords do not match.'); return; }
    if (form.new_password.length < 6)                { setError('New password must be at least 6 characters.'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword({ old_password: form.current_password, new_password: form.new_password });
      setSuccess('Password changed successfully.');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {error   && <div className="rounded-xl border border-red-200   bg-red-50   px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <PasswordField label="Current Password"  name="current_password" value={form.current_password} onChange={handleChange} />
        <div className="grid gap-4 sm:grid-cols-2">
          <PasswordField label="New Password"     name="new_password"     value={form.new_password}     onChange={handleChange} />
          <PasswordField label="Confirm Password" name="confirm_password" value={form.confirm_password} onChange={handleChange} />
        </div>
        <button type="submit" disabled={saving}
          className="rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-60">
          {saving ? 'Saving...' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

function PasswordField({ label, name, value, onChange }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <input type="password" name={name} value={value} onChange={onChange} required
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
    </div>
  );
}
